import { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { getFirestoreDB } from "@/firebase";

export type PendingSurvey = {
    id: string;
    name: string;
    summary: string;
    link: string;
    actionType?: "link" | "iframe";
    priority?: number;
};

function sortPendingSurveys(surveys: PendingSurvey[]) {
    return [...surveys].sort((left, right) => {
        const leftPriority = left.priority ?? Number.MAX_SAFE_INTEGER;
        const rightPriority = right.priority ?? Number.MAX_SAFE_INTEGER;

        if (leftPriority !== rightPriority) {
            return leftPriority - rightPriority;
        }

        return left.name.localeCompare(right.name);
    });
}

export async function getAvailableSurveys(uid: string) {
    const db = getFirestoreDB();
    const userProfileRef = doc(db, "userProfile", uid);
    const completedActionsRef = collection(userProfileRef, "completedActions");
    const actionsRef = collection(db, "trialFinderUserActions");

    const [completedActionsSnapshot, userProfileSnapshot, actionsSnapshot] = await Promise.all([
        getDocs(completedActionsRef),
        getDoc(userProfileRef),
        getDocs(actionsRef),
    ]);

    if (!userProfileSnapshot.exists()) {
        return [];
    }

    const completedActionIds = new Set(completedActionsSnapshot.docs.map((snapshot) => snapshot.id));
    const surveyIds = new Set<string>();
    const availableSurveys: PendingSurvey[] = [];

    actionsSnapshot.docs.forEach((snapshot) => {
        if (completedActionIds.has(snapshot.id) || surveyIds.has(snapshot.id)) {
            return;
        }

        surveyIds.add(snapshot.id);
        const data = snapshot.data() as Partial<Omit<PendingSurvey, "id">>;

        availableSurveys.push({
            id: snapshot.id,
            name: data.name ?? snapshot.id,
            summary: data.summary ?? "",
            link: data.link ?? "",
            actionType: data.actionType || "link",
            priority: data.priority,
        });
    });

    return sortPendingSurveys(availableSurveys);
}

export function useAvailableSurveys(uid: string | undefined, initialSurveys: PendingSurvey[] = []) {
    const [surveys, setSurveys] = useState<PendingSurvey[]>(sortPendingSurveys(initialSurveys));
    const [isLoading, setIsLoading] = useState(initialSurveys.length === 0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isActive = true;

        if (!uid) {
            if (initialSurveys.length === 0) {
                setSurveys([]);
            }
            setIsLoading(false);
            return;
        }

        const load = async () => {
            if (initialSurveys.length === 0) {
                setIsLoading(true);
            }
            setError(null);
            try {
                const availableSurveys = await getAvailableSurveys(uid);
                if (isActive) setSurveys(availableSurveys);
            } catch (err) {
                console.error("Unable to load available surveys", err);
                if (isActive) {
                    setError("We couldn't load your pending actions right now. Please try again.");
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            isActive = false;
        };
    }, [uid, initialSurveys.length]);

    return { surveys, isLoading, error };
}
